import type { AppDispatch } from "app/store";
import { telemetry } from "telemetry";
import { AsyncStatus } from "types/misc";

import { setDrawingData, setDrawingPreview } from "../minimapSlice";
import type { Uuid } from "../types";
import { type ParsedManifestUrl, resolveTiledDrawingSource } from "./tiledSource";

/**
 * Attempt the tiled route: fetch the ~1 KB manifest, register the tile
 * source, and flip both async states to `Success` with the natural
 * dimensions (empty blob payloads — pixels stream on demand via
 * `useTiledDrawingImage` / `useTiledLargeMap`).
 *
 * Returns `null` on success, the failure `Error` otherwise. Dispatches
 * neither `Loading` nor `Error`: the caller (`downloadDrawingData`) owns
 * those, since a tiled failure may fall back to the PDF route.
 */
export async function downloadTiledDrawingData({
    dispatch,
    drawingId,
    url,
    manifest,
    telemetrySource,
}: {
    dispatch: AppDispatch;
    drawingId: Uuid;
    url: string;
    manifest: ParsedManifestUrl;
    telemetrySource: string;
}): Promise<Error | null> {
    try {
        const { width, height } = await resolveTiledDrawingSource(url, manifest);
        dispatch(
            setDrawingPreview({
                drawingId,
                asyncState: { status: AsyncStatus.Success, data: { preview: new Blob([]), width, height } },
            }),
        );
        dispatch(
            setDrawingData({
                drawingId,
                asyncState: { status: AsyncStatus.Success, data: { tiles: [], width, height } },
            }),
        );
        return null;
    } catch (error) {
        // Warning, not error: the caller may recover via the PDF fallback.
        telemetry.trackCaughtException({
            source: telemetrySource,
            category: "render",
            error,
            severity: "warning",
            extraAttributes: { drawingId },
        });
        return error instanceof Error ? error : new Error(String(error));
    }
}
